import React from 'react';
import Modal from './Modal';
import Button from './Button';

export interface ConfirmationDialogProps {
    /**
     * Whether the dialog is open
     */
    isOpen: boolean;

    /**
     * Title of the confirmation dialog
     */
    title: string;

    /**
     * Message to display in the confirmation dialog
     */
    message: string | React.ReactNode;

    /**
     * Text for the confirm button
     */
    confirmText?: string;

    /**
     * Text for the cancel button
     */
    cancelText?: string;

    /**
     * Variant for the confirm button
     */
    confirmVariant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'outline-dark';

    /**
     * Variant for the cancel button
     */
    cancelVariant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'outline-dark';

    /**
     * Callback when the user confirms
     */
    onConfirm: () => void;

    /**
     * Callback when the user cancels
     */
    onCancel: () => void;

    /**
     * Additional class name for the modal
     */
    className?: string;

    /**
     * Icon to display before the message
     */
    icon?: React.ReactNode;

    /**
     * Whether the confirm action is currently in progress
     */
    isConfirmLoading?: boolean;
}

/**
 * A reusable confirmation dialog component that replaces window.confirm
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    isOpen,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmVariant = 'danger',
    cancelVariant = 'outline-dark',
    onConfirm,
    onCancel,
    className = '',
    icon,
    isConfirmLoading = false
}) => {
    return (
        <Modal
            className={className}
            footer={
                <>
                    <Button disabled={isConfirmLoading} variant={cancelVariant} onClick={onCancel}>
                        {cancelText}
                    </Button>
                    <Button isLoading={isConfirmLoading} variant={confirmVariant} onClick={onConfirm}>
                        {confirmText}
                    </Button>
                </>
            }
            isOpen={isOpen}
            title={title}
            onClose={onCancel}
        >
            <div className="flex items-start">
                {icon && <div className="mr-3 flex-shrink-0 text-2xl">{icon}</div>}
                <div className="text-zinc-300">{typeof message === 'string' ? <p>{message}</p> : message}</div>
            </div>
        </Modal>
    );
};

export default ConfirmationDialog;
